import { useState } from "react";
import axios from "axios";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OPTIONS = {
  trabalha_salao: { label: "Você trabalha em salão?", items: ["Sim", "Não"] },
  tempo_beleza: { label: "Há quanto tempo você trabalha com beleza?", items: ["Menos de 1 ano", "De 1 a 5 anos", "De 5 a 10 anos", "Mais de 10 anos"] },
  equipe: { label: "Quantas pessoas tem na equipe?", items: ["Só eu", "2 a 3 pessoas", "4 a 10 pessoas", "Mais de 10 pessoas"] },
  faturamento: { label: "Qual é o seu faturamento mensal?", items: ["Até R$ 5 mil", "De R$ 5 mil a R$ 15 mil", "De R$ 15 mil a R$ 30 mil", "De R$ 30 mil a R$ 60 mil", "Acima de R$ 60 mil"] },
};

const maskPhone = (value) => {
  const d = value.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

const initial = { nome: "", email: "", whatsapp: "", trabalha_salao: "", tempo_beleza: "", equipe: "", faturamento: "", consentimento: false };

const inputClass =
  "w-full rounded-xl border border-charcoal/15 bg-white px-4 py-3.5 text-base text-charcoal placeholder:text-charcoal/35 transition-colors focus:border-salon focus:outline-none";

export const Lancamento = () => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.nome.trim().length < 2) e.nome = "Informe seu nome.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Informe um e-mail válido.";
    if (form.whatsapp.replace(/\D/g, "").length < 10) e.whatsapp = "Informe seu WhatsApp com DDD.";
    for (const key of Object.keys(OPTIONS)) if (!form[key]) e[key] = "Escolha uma opção.";
    if (!form.consentimento) e.consentimento = "É preciso aceitar para continuar.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
    } catch {
      setErrors({ envio: "Não foi possível enviar. Tente novamente." });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="lancamento" data-testid="lancamento-section" className="border-t border-charcoal/[0.06] bg-neutral-light py-24 md:py-32">
      <div className="mx-auto max-w-xl px-6">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-salon">Lista de lançamento</p>
          <h2 className="mt-5 font-display text-3xl font-medium tracking-tight text-charcoal md:text-4xl">
            Seja avisado do lançamento
          </h2>
        </Reveal>

        {done ? (
          <Reveal className="mt-10 rounded-2xl border border-salon/20 bg-salon-mist px-6 py-8">
            <p data-testid="lancamento-sucesso" className="text-base font-medium text-salon-deep">
              Pronto! Avisaremos você assim que o SalonUP for lançado.
            </p>
          </Reveal>
        ) : (
          <Reveal delay={0.1}>
            <form onSubmit={submit} noValidate className="mt-10 space-y-7" data-testid="lancamento-form">
              <div>
                <label htmlFor="lead-nome" className="mb-2 block text-sm font-medium text-charcoal">Nome</label>
                <input id="lead-nome" data-testid="lead-nome" type="text" value={form.nome} onChange={(e) => set("nome", e.target.value)} className={inputClass} autoComplete="name" />
                {errors.nome && <p className="mt-1.5 text-xs text-red-700">{errors.nome}</p>}
              </div>

              <div>
                <label htmlFor="lead-email" className="mb-2 block text-sm font-medium text-charcoal">E-mail</label>
                <input id="lead-email" data-testid="lead-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputClass} autoComplete="email" />
                {errors.email && <p className="mt-1.5 text-xs text-red-700">{errors.email}</p>}
              </div>

              <div>
                <label htmlFor="lead-whatsapp" className="mb-2 block text-sm font-medium text-charcoal">WhatsApp</label>
                <input id="lead-whatsapp" data-testid="lead-whatsapp" type="tel" inputMode="numeric" placeholder="(11) 99999-9999" value={form.whatsapp} onChange={(e) => set("whatsapp", maskPhone(e.target.value))} className={inputClass} autoComplete="tel" />
                {errors.whatsapp && <p className="mt-1.5 text-xs text-red-700">{errors.whatsapp}</p>}
              </div>

              {Object.entries(OPTIONS).map(([key, { label, items }]) => (
                <fieldset key={key}>
                  <legend className="mb-2.5 text-sm font-medium text-charcoal">{label}</legend>
                  <div className="flex flex-wrap gap-2.5">
                    {items.map((item) => (
                      <button
                        key={item}
                        type="button"
                        data-testid={`lead-${key}-${item.replace(/\W+/g, "-").toLowerCase()}`}
                        aria-pressed={form[key] === item}
                        onClick={() => set(key, item)}
                        className={`min-h-[44px] rounded-full border px-4 py-2.5 text-sm transition-colors duration-200 ${
                          form[key] === item
                            ? "border-salon bg-salon text-white"
                            : "border-charcoal/15 bg-white text-charcoal/70 hover:border-charcoal/35"
                        }`}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                  {errors[key] && <p className="mt-1.5 text-xs text-red-700">{errors[key]}</p>}
                </fieldset>
              ))}

              <div>
                <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-charcoal/70">
                  <input
                    type="checkbox"
                    data-testid="lead-consentimento"
                    checked={form.consentimento}
                    onChange={(e) => set("consentimento", e.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 accent-[#1F5132]"
                  />
                  <span>
                    Aceito receber o aviso de lançamento do SalonUP por WhatsApp e e-mail e concordo com a{" "}
                    <a href="/privacidade" data-testid="lead-privacidade-link" className="underline underline-offset-2 hover:text-charcoal">
                      política de privacidade
                    </a>
                    .
                  </span>
                </label>
                {errors.consentimento && <p className="mt-1.5 text-xs text-red-700">{errors.consentimento}</p>}
              </div>

              {errors.envio && <p className="text-sm text-red-700">{errors.envio}</p>}

              <button
                type="submit"
                data-testid="lancamento-submit"
                disabled={sending}
                className="inline-flex min-h-[48px] w-full items-center justify-center rounded-full bg-salon px-7 py-3.5 text-sm font-medium text-white transition-colors duration-300 hover:bg-salon-deep disabled:opacity-60 md:w-auto"
              >
                {sending ? "Enviando..." : "Quero ser avisado"}
              </button>
            </form>
          </Reveal>
        )}
      </div>
    </section>
  );
};
